// Brand welcome wizard. First-login destination for brands whose account
// the operator just provisioned via the inquiry flow.
//
// Two steps:
//   1. Confirm company info (name + website pre-filled from inquiry).
//   2. Tell us when's good for a kickoff call.
//
// On completion, stamps brands.onboarded_at and drops the user into /app.
// Mounted from /app/welcome. Home.tsx redirects unbonarded brands here.
//
// Visual: Orbis Brand Kit v1.0. Geist sans, ink #08080A, signal #5EFC8D.
// No serifs, no em-dashes, no exclamation marks.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { brandApi } from '../../lib/api';

type Step = 'confirm' | 'kickoff' | 'done';

// Operator's (Kevin's) Google Calendar appointment booking page. Same link
// used across the onboarding email drips + reply worker (KEVIN_BOOKING_URL).
const BOOKING_URL = 'https://calendar.app.google/XsKcAKz2yaGe141R8';

export function Welcome() {
  const navigate = useNavigate();

  // Pre-fill from /api/brand/campaigns (which now returns profile too).
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [kickoffNote, setKickoffNote] = useState('');
  const [step, setStep] = useState<Step>('confirm');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('orbis-kit');
    return () => document.body.classList.remove('orbis-kit');
  }, []);

  useEffect(() => {
    brandApi
      .campaigns()
      .then((d) => {
        const p = (d as { profile?: { name?: string; website?: string | null; onboarded_at?: string | null } }).profile;
        if (p) {
          setCompanyName(p.name ?? '');
          setWebsite(p.website ?? '');
          // If already onboarded somehow (race / refresh after submit),
          // bounce straight to the dashboard. Don't double-collect.
          if (p.onboarded_at) {
            navigate('/app', { replace: true });
            return;
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  async function saveProfile() {
    setBusy(true);
    setError(null);
    try {
      await brandApi.updateProfile({ name: companyName.trim(), website: website.trim() });
      setStep('kickoff');
    } catch (e) {
      setError(prettyApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    setBusy(true);
    setError(null);
    const note = kickoffNote.trim();
    try {
      await brandApi.completeOnboarding({ kickoff_when: note || undefined });
      setStep('done');
      // Tiny pause so the "done" state is felt, then route to /app.
      setTimeout(() => navigate('/app', { replace: true }), 1100);
    } catch (e) {
      setError(prettyApiError(e));
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Shell>
        <div style={{ padding: 64, textAlign: 'center', color: 'var(--okit-ink-mute)' }}>
          Loading.
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Progress step={step} />

      {step === 'confirm' && (
        <Card>
          <Eyebrow>Step 1 of 2</Eyebrow>
          <H1>Confirm your company.</H1>
          <Lead>
            We pre-filled this from your inquiry. Adjust anything that needs to change.
          </Lead>

          <FieldSpacer>
            <Field
              label="Company name"
              value={companyName}
              onChange={setCompanyName}
              placeholder="Acme Records"
              autoFocus
            />
            <Field
              label="Website or main social"
              value={website}
              onChange={setWebsite}
              placeholder="acme.com or @acme"
            />
          </FieldSpacer>

          {error && <ErrorLine text={error} />}

          <ActionRow>
            <PrimaryButton
              onClick={saveProfile}
              disabled={!companyName.trim() || busy}
              busy={busy}
            >
              Looks right. Continue.
            </PrimaryButton>
          </ActionRow>
        </Card>
      )}

      {step === 'kickoff' && (
        <Card>
          <Eyebrow>Step 2 of 2 · optional</Eyebrow>
          <H1>Want a kickoff call?</H1>
          <Lead>
            Optional. Thirty minutes to align on goals, budget, and timing for your
            first campaign. Grab a time that works, or skip and we'll reach out.
          </Lead>

          <FieldSpacer>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '13px 22px',
                  background: 'var(--okit-surface)',
                  color: 'var(--okit-ink)',
                  border: '1px solid var(--okit-line-strong)',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Book a kickoff call ↗
              </span>
            </a>
            <Field
              label="Anything we should know first (optional)"
              value={kickoffNote}
              onChange={setKickoffNote}
              placeholder="Time zone, days that work, decision-maker on the call, etc."
              textarea
            />
          </FieldSpacer>

          {error && <ErrorLine text={error} />}

          <ActionRow>
            <GhostButton onClick={() => setStep('confirm')} disabled={busy}>
              Back
            </GhostButton>
            <PrimaryButton onClick={finish} disabled={busy} busy={busy}>
              {kickoffNote.trim() ? 'Finish setup' : 'Skip and finish'}
            </PrimaryButton>
          </ActionRow>
        </Card>
      )}

      {step === 'done' && (
        <Card>
          <SignalDot />
          <H1>You're in.</H1>
          <Lead>Taking you to your dashboard.</Lead>
        </Card>
      )}
    </Shell>
  );
}

function prettyApiError(e: unknown): string {
  const s = String(e);
  if (s.includes('invalid_name')) return 'Company name is required.';
  if (s.includes('invalid_website')) return 'That website looks off. Try again.';
  if (s.includes('no_brand')) return 'Your account is not linked to a brand yet. Contact us.';
  return 'Something went wrong. Try again, or email hello@imorbis.com.';
}

// ── Building blocks (Brand Kit) ────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SystemBar />
      <main
        style={{
          minHeight: '100vh',
          padding: '64px 24px 96px',
          background: 'var(--okit-bg)',
        }}
      >
        <div style={{ maxWidth: 600, margin: '0 auto' }}>{children}</div>
      </main>
    </>
  );
}

function SystemBar() {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: '12px 24px',
        background: 'var(--okit-ink)',
        color: 'var(--okit-bg)',
        fontFamily: "'Geist Mono', monospace",
        fontSize: 12,
        letterSpacing: '-0.005em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span style={{ fontWeight: 600 }}>orbis · studio</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#9CA0A6' }}>
        <Pulse /> brand · onboarding
      </span>
    </header>
  );
}

function Pulse() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: 'var(--okit-signal)',
        boxShadow: '0 0 0 0 rgba(94, 252, 141, 0.6)',
        animation: 'okitPulse 1.6s ease-out infinite',
      }}
    />
  );
}

function Progress({ step }: { step: Step }) {
  const idx = step === 'confirm' ? 0 : step === 'kickoff' ? 1 : 2;
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        marginBottom: 28,
      }}
    >
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: 3,
            borderRadius: 999,
            background: i <= idx ? 'var(--okit-ink)' : 'var(--okit-line)',
            transition: 'background 240ms ease',
          }}
        />
      ))}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--okit-surface)',
        border: '1px solid var(--okit-line)',
        borderRadius: 14,
        padding: 40,
      }}
    >
      {children}
    </section>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "'Geist Mono', monospace",
        fontSize: 11,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--okit-ink-mute)',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

function H1({ children }: { children: React.ReactNode }) {
  return (
    <h1
      style={{
        fontFamily: 'inherit',
        fontSize: 'clamp(28px, 3.6vw, 36px)',
        fontWeight: 600,
        letterSpacing: '-0.025em',
        color: 'var(--okit-ink)',
        lineHeight: 1.1,
        margin: 0,
      }}
    >
      {children}
    </h1>
  );
}

function Lead({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        marginTop: 14,
        fontSize: 16,
        lineHeight: 1.55,
        color: 'var(--okit-ink-soft)',
        maxWidth: '52ch',
      }}
    >
      {children}
    </p>
  );
}

function FieldSpacer({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 18 }}>{children}</div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  textarea,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  textarea?: boolean;
  autoFocus?: boolean;
}) {
  const sharedStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    background: 'var(--okit-bg)',
    color: 'var(--okit-ink)',
    border: '1px solid var(--okit-line-strong)',
    borderRadius: 10,
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
  };
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--okit-ink-soft)',
          marginBottom: 8,
        }}
      >
        {label}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ ...sharedStyle, resize: 'vertical', lineHeight: 1.5 }}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          style={sharedStyle}
        />
      )}
    </div>
  );
}

function OptionGrid({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            style={{
              padding: '14px 16px',
              background: selected ? 'var(--okit-ink)' : 'var(--okit-bg)',
              color: selected ? 'var(--okit-bg)' : 'var(--okit-ink)',
              border: `1px solid ${selected ? 'var(--okit-ink)' : 'var(--okit-line-strong)'}`,
              borderRadius: 10,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 140ms ease, color 140ms ease, border-color 140ms ease',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ActionRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 28, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
      {children}
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  busy,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '13px 22px',
        background: 'var(--okit-ink)',
        color: 'var(--okit-bg)',
        border: 'none',
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'opacity 140ms ease, transform 140ms ease',
      }}
    >
      {busy ? 'Working.' : children}
    </button>
  );
}

function GhostButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '13px 22px',
        background: 'transparent',
        color: 'var(--okit-ink)',
        border: '1px solid var(--okit-line-strong)',
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {children}
    </button>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <p
      style={{
        marginTop: 16,
        padding: '10px 12px',
        background: 'rgba(212, 58, 47, 0.06)',
        border: '1px solid rgba(212, 58, 47, 0.25)',
        borderRadius: 8,
        color: 'var(--okit-warn)',
        fontSize: 13,
      }}
    >
      {text}
    </p>
  );
}

function SignalDot() {
  return (
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'var(--okit-signal)',
        margin: '0 auto 22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path
          d="M5 11.5l4 4 8-9"
          stroke="#08080A"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
